import prepareAssertions from '~/packages/variable/prepareAssertions.util'
import { Variable } from '~/packages/variable/variables.typing'
import { boolean, dateTime, defineAction, enumerated, number, object, string } from '~/prix'
import expression from '~/prix/types/expression'
import { analysis } from '~/prix/types/presets/analysis'
import { assertions } from '~/prix/types/presets/assertions'
import { select } from '~/prix/types/presets/select'
import { childrenType, spaceForPoint } from '~/prix/types/presets/space'
import { timeForIncidence } from '~/prix/types/presets/time'
import { variable } from '~/prix/types/presets/variable'
import appSebraeAttendanceStatsAction from './appSebraeAttendanceStats.action.json' with { type: 'json' }
import prefixProperties from '~/prix/utils/prefixProperties'
import cnaeDivision from '~/packages/cnaeDivision/cnaeDivision.entity'
import { pick } from '~/prix/utils/types/object'
import getRecommendedCacheOptions from '~/utils/server/stats/getRecommendedCacheOptions'
import { defaultDetectionLayers } from '~/utils/server/stats/defaultTimeSeriesLayers'

const attendanceDataShape = object({
  remoteJustification: string().isOptional().label('Justificativa remota'),
  regional: string().isOptional().label('Regional'),
  actionId: number().isOptional().label('ID da ação'),
  actionDescription: string().isOptional().label('Descrição da ação'),
  actionCodePratif: string().isOptional().label('Código Pratif da ação'),
  actionNamePratif: string().isOptional().label('Nome Pratif da ação'),
  actionCode: string().isOptional().label('Código da ação'),
  actionName: string().isOptional().label('Nome da ação'),
  actionActive: boolean().isOptional().label('Ação ativa'),
  actionCodeSequence: number().isOptional().label('Código sequencial da ação'),
  actionProgramCode: string().isOptional().label('Código do programa da ação'),
  actionStatus: string().isOptional().label('Status da ação'),
  generalPercentage: number().isOptional().label('Percentual geral'),
  classification: string().isOptional().label('Classificação'),
  generalStatus: string().isOptional().label('Status geral'),
  themes: string().isOptional().label('Temas'),
})

const combinedShape = object({
  agentCpf: string().label('CPF do agente'),
  agentName: string().label('Nome do agente'),
  startDate: dateTime().label('Data de início do atendimento'),
  agentPolygonCpf: string().label('Polígono do agente'),
  regional: string().isOptional().label('Regional'),

  //legalEntity
  id: string().label('ID', 'Identificador', 'Código'),
  tradeName: string().label('Nome fantasia'),
  corporateName: string().isOptional().label('Razão social'),
  cnpj: string().label('CNPJ'),
  openingDate: dateTime().label('Data de abertura'),
  mainCnaeId: string().label('Identificador do CNAE'),
  mainCnaeDescription: string().isOptional().label('Descrição do CNAE principal'),
  segmentId: string().label('Identificador do segmento'),
  segmentDescription: string().isOptional().label('Descrição do segmento'),
  cnaes: string().isOptional().label('CNAEs secundários'),
  legalNatureId: string().isOptional().label('Identificador da natureza jurídica'),
  legalNatureDescription: string().isOptional().label('Descrição da natureza jurídica'),
  email: string().label('Email'),
  street: string().label('Logradouro'),
  number: string().isOptional().label('Número'),
  city: string().label('Cidade'),
  neighborhood: string().label('Bairro'),
  state: string().label('Estado'),
  registrationStatus: enumerated({
    active: 'Ativo',
    inapt: 'Inapto',
    suspended: 'Suspenso',
    extinct: 'Extinto',
  }).label('Status'),
  registrationStatusDate: dateTime().isOptional().label('Data do Registro'),
  registrationStatusReason: string().label('Motivo do status de registro'),
  size: enumerated({
    MEI: 'MEI',
    ME: 'ME',
    EPP: 'EPP',
    other: 'Outros',
    unknown: 'Desconhecido',
  }).label('Porte'),
  sizeAbbreviation: enumerated({
    MEI: 'MEI',
    ME: 'ME',
    EPP: 'EPP',
    other: 'Outros',
    unknown: 'Desconhecido',
  }).label('Sigla do porte'),
  isSimples: boolean().label('É Simples Nacional'),
  isMei: boolean().isOptional().label('É MEI'),
  capital: number().label('Capital social'),
  ageGroup: enumerated({
    startup: 'Startup',
    young: 'Jovem',
    growth: 'Crescimento',
    established: 'Estabelecida',
  }).label('Maturidade'),

  fullAddress: string().label('Endereço'),
  phoneNumber1AreaCode: string().isOptional().label('DDD do telefone principal'),
  fullPhoneNumber1: string().label('Telefone principal'),
  fullPhoneNumber2: string().label('Telefone secundário'),

  // geo
  pointOnStreet: object({
    type: string().label('Tipo'),
    coordinates: object({
      0: number().label('Longitude'),
      1: number().label('Latitude'),
    }),
  }).label('Ponto na rua'),

  ...prefixProperties('attendanceData.', attendanceDataShape.shape),
  ...prefixProperties('cnaeDivision.', cnaeDivision.schema.shape),
})
export const appSebraeAttendanceExpression = expression(combinedShape.shape)

export const appSebraeAttendanceVariable: Variable = {
  key: 'appSebraeAttendance',
  categoryKey: 'appSebraeAttendance',
  categoryKeys: ['appSebraeAttendance'],
  actionDefinitionKey: appSebraeAttendanceStatsAction.key,
  title: 'Atendimentos de Agentes SEBRAE Na Sua Empresa',
  label: 'Geral',
  unitSingular: 'atendimento',
  unitPlural: 'atendimentos',
  grammaticalGender: 'male',
  timeType: 'incidence',
  shape: combinedShape,
  betterDensity: 'more',
  valueRules: {
    minValue: 0,
  },
  relevance: 0.8,
  payload: {
    productName: 'Agentes SEBRAE Na Sua Empresa',
    portfolioName: ['App SEBRAE'],
  },
  basicSelect: ['corporateName', 'startDate'],
  exportSortBy: { key: 'startDate', direction: 'desc' },
  detailedSelect: [
    'id',
    'cnpj',
    'tradeName',
    'corporateName',
    'registrationStatus',
    'registrationStatusDate',
    'openingDate',
    'mainCnaeId',
    'mainCnaeDescription',
    'segmentId',
    'segmentDescription',
    'cnaes',
    'legalNatureId',
    'legalNatureDescription',
    'size',
    'ageGroup',
    'isSimples',
    'isMei',
    'email',
    'street',
    'number',
    'neighborhood',
    'city',
    'state',
    'phoneNumber1AreaCode',
    'fullPhoneNumber1',
    'fullPhoneNumber2',
    'agentName',
    'regional',
    'attendanceData.actionId',
    'attendanceData.actionDescription',
    'attendanceData.actionCodePratif',
    'attendanceData.actionNamePratif',
    'attendanceData.actionCode',
    'attendanceData.actionName',
    'attendanceData.actionActive',
    'attendanceData.actionCodeSequence',
    'attendanceData.actionProgramCode',
    'attendanceData.actionStatus',
    'attendanceData.generalPercentage',
    'attendanceData.classification',
    'attendanceData.generalStatus',
    'attendanceData.themes',
    'startDate',
  ],
  filterableBy: expression(
    pick(combinedShape.shape, [
      'size',
      'ageGroup',
      'segmentId',
      'registrationStatus',
      'agentCpf',
      'agentPolygonCpf',
      'regional',
    ]),
  ),

  spaceAnalysis: undefined,

  timeSeries: {
    model: 'additive',
    fitDays: 365 * 10,
    predictionCacheVersion: 7,
    predictionHistoricalEnvelope: {
      upperQuantile: 0.9,
      multiplier: 1.15,
      lowerQuantile: 0.35,
      floorMultiplier: 0.8,
      minCollapsedDays: 21,
      minPositiveSamples: 3,
      collapseThresholdRatio: 0.15,
    },
    layers: [
      ...defaultDetectionLayers,

      // Camadas de tendência e sazonalidade
      { layer: 'trend', degree: 2, predictionDegree: 1 },
      {
        layer: 'calendarSeasonality',
        type: 'dayOfWeek',
        degree: 1,
        optimization: {
          minimumDays: 60,
          lossFunction: 'recentWeightedMeanSquaredError',
        },
      },
      { layer: 'calendarSeasonality', type: 'year', degree: 2 },
      { layer: 'calendarSeasonality', type: 'holiday', degree: 1 },
      { layer: 'calendarSeasonality', type: 'monthOfYear', degree: 0 },
      { layer: 'calendarSeasonality', type: 'year', degree: 1 },
      { layer: 'trend', degree: 1 },
      { layer: 'calendarSeasonality', type: 'monthOfYear', degree: 1 },
      { layer: 'calendarSeasonality', type: 'dayOfMonth', degree: 1 },
      {
        layer: 'harmonicSeasonality',
        optimization: {
          minimumDays: 45,
          lossFunction: 'recentWeightedMeanSquaredError',
        },
      },
    ],
  },
  assertions: prepareAssertions(appSebraeAttendanceExpression),
}

const appSebraeAttendanceStatsDefinition = defineAction({
  ...appSebraeAttendanceStatsAction,
  input: object({
    variable,
    highlight: appSebraeAttendanceExpression.isOptional(),
    filter: appSebraeAttendanceExpression.isOptional(),
    space: spaceForPoint().isOptional(),
    time: timeForIncidence().isOptional(),
    select: select(combinedShape),
    assertions,
    analysis,
  }),
  output: object({
    items: object({
      value: number(),
      numerator: number().isOptional(),
      denominator: number().isOptional(),
      geoId: string().isOptional(),
      geoType: childrenType.isOptional(),
      dateTime: string().isOptional(),
    }),
  }),
  cache: getRecommendedCacheOptions,
})

export default appSebraeAttendanceStatsDefinition
